/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { StringParam, useQueryParam, ArrayParam } from "use-query-params"
import Select from "react-select"

import AnchorButton from "components/AnchorButton"
import FilterDropdown from "components/FilterDropdown"
import Loader from "components/Loader"
import SearchInput from "components/SearchInput"
import CollapsibleSection from "components/CollapsibleSection"
import { getFieldWorkJobServices, getFieldWorkDatesList } from "requesters/jobServiceRequester"
import { getFilteredProducts } from "requesters/productRequester"
import { getAssignedSurveyorMembers, getSurveyors } from "requesters/userRequester"
import { Link } from "react-router-dom"

import {
  ACCOUNTS,
  ADMIN,
  ADMIN_3D,
  DATA_OVERDUE,
  GPS_REQUESTED,
  JOB_PACK_INCOMPLETE,
  OPERATIONS,
  OPERATION_MANAGER,
  RECEPTION,
  RECEPTION_MANAGER,
  SALES,
  SURVEYORS,
  SURVEYORS_MANAGER,
  SURVEYORS_USER,
} from "utils/constants"


import {
  addDays, endOfDay,
  format, startOfDay, isPast
} from "date-fns"
import _ from "lodash"
import { roleCheck } from "utils/index"
import { fitSchedulesTablesToView } from "utils/tableZoomEnhancer"
import Calendar from "../../components/Calendar/Calendar"
import { getBaseUrl, getCurrentRole, getCurrentUser } from "../../reducers"
import FollowUpbookingModal from "../JobDetails/modals/FollowUpbookingModal"
import BlockOutsTable from "./BlockOutsTable"
import SchedulesDateTable from "./SchedulesDateTable"

export default function SchedulesPage() {
  const baseUrl = useSelector(getBaseUrl)
  const currentRole = useSelector(getCurrentRole)
  const currentUser = useSelector(getCurrentUser)
  const systemRole = currentUser.attributes.role
  const { attributes: { role } } = currentUser
  const currentTeamRole = currentUser.attributes.current_team_role
  const readOnly = !((/admin/i.test(role) || currentTeamRole.includes(OPERATION_MANAGER)))
  const receptionTeam = currentTeamRole.includes(RECEPTION_MANAGER)
  const isAdmin = systemRole === ADMIN
  const isSurveyorsManager = !isAdmin && roleCheck(systemRole, currentTeamRole, SURVEYORS_MANAGER)
  const isSurveyor = !isAdmin && (
    systemRole === SURVEYORS
    || (Array.isArray(currentTeamRole) && (currentTeamRole.includes(SURVEYORS) || currentTeamRole.includes(SURVEYORS_USER)))
  )
  const isSales = roleCheck(systemRole, currentRole, SALES)
  const isAccounts = roleCheck(systemRole, currentRole, ACCOUNTS)
  const [jobTypes, setJobTypes] = useState([])
  const [surveyorOptions, setSurveyorOptions] = useState([])
  const [surveyorSelectOptions, setSurveyorSelectOptions] = useState([])
  const [selectedSurveyorOptions, setSelectedSurveyorOptions] = useState([])
  const [selectedSurveyorIds, setSelectedSurveyorIds] = useQueryParam("surveyor_ids", ArrayParam)

  const liveJobsAllowed = () => {
    if (roleCheck(systemRole, currentRole, OPERATIONS) || roleCheck(systemRole, currentRole, SALES) || roleCheck(systemRole, currentRole, SURVEYORS) || roleCheck(systemRole, currentRole, RECEPTION) || roleCheck(systemRole, currentRole, ACCOUNTS) || roleCheck(systemRole, currentRole, ADMIN_3D)) {
      return true
    }
    return false
  }

  if (!liveJobsAllowed()) window.location = "/"

  const jobStatusOptions = [
    ["Data Overdue", "data_overdue"],
    ["Job Pack Incomplete", "job_pack_incomplete"],
    ["Awaiting Payment", "awaiting_payment"],
    ["GPS Requested", "gps_requested"],
    ["Field Work Ongoing", "field_work_ongoing"],
    ["Field Work Finished", "field_work_finished"],
    ["Field Work Incompleted", "field_work_incompleted"],
    ["Rescheduled", "rescheduled"],
    ["To Confirm", "to_confirm"],
    ["Pending", "pending"],
    ["Confirmed", "confirmed"],
    ["GPS Declined", "gps_declined"],
    ["GPS Completed", "gps_completed"],
    ["GPS Approved", "gps_approved"]
  ]

  const params = (new URL(document.location)).searchParams;
  const filter = params.get("filter");

  const jobPackIncompleteFilter = () => {
    if (filter === JOB_PACK_INCOMPLETE) {
      return JOB_PACK_INCOMPLETE
    }
    if (filter === DATA_OVERDUE) {
      return DATA_OVERDUE
    }
    if (filter === GPS_REQUESTED) {
      return GPS_REQUESTED
    }
    return undefined
  }

  const bySurveyorFilter = () => {
    if (receptionTeam || isSales || isAccounts) {
      return []
    }
    if (readOnly && !isSurveyorsManager) {
      return [currentUser.id]
    }
    return selectedSurveyorIds?.map(id => typeof id === 'string' ? parseInt(id, 10) : id) || []
  }

  const initialFilters = {
    by_status: jobPackIncompleteFilter(),
    by_surveyor_ids: bySurveyorFilter(),
    by_job_type: undefined,
    by_keyword: undefined,
    by_booking_time: [
      startOfDay(localStorage.getItem("selectedDate") ? new Date(localStorage.getItem("selectedDate")) : new Date()),
      endOfDay(addDays(localStorage.getItem("selectedDate") ? new Date(localStorage.getItem("selectedDate")) : new Date(), 7))
    ]
  }

  const [filters, setFilters] = useState(initialFilters)
  const [isFilteredByDropDown, setIsFilteredByDropDown] = useState(false)

  const byStatusName = _.get(jobStatusOptions, `${_.findIndex(jobStatusOptions, option => option[1] === filters.by_status)}.0`)
  const byJobTypeName = _.get(jobTypes, `${_.findIndex(jobTypes, type => type[1] === filters.by_job_type)}.0`)

  const [calendarDateString, setCalendarDateString] = useQueryParam("date", StringParam)
  const [targetId, setTargetId] = useQueryParam("target_id", StringParam)
  const [calendarDate, setCalendarDate] = useState(localStorage.getItem("selectedDate") ? new Date(localStorage.getItem("selectedDate")) : new Date())
  const [hidePast, setHidePast] = useState(false)
  // Expand all date sections by default
  const [expandAll, setExpandAll] = useState(true)

  const [showFollowUpbookingModal, setShowFollowUpbookingModal] = useState(false)

  // State for collapsible date sections
  const SCHEDULE_EXPANDED_KEY = "schedules_expanded_dates"
  const SCHEDULE_LAST_EXPANDED_KEY = "schedules_last_expanded_date"

  const [datesList, setDatesList] = useState([])
  const [dataByDate, setDataByDate] = useState({})
  const [expandedDates, setExpandedDates] = useState({})
  const expandedDatesRef = useRef(expandedDates)
  const [loadingDates, setLoadingDates] = useState({})
  const [initialLoading, setInitialLoading] = useState(true)
  const [scrollToDateKey, setScrollToDateKey] = useState(null)

  expandedDatesRef.current = expandedDates

  const clearFilters = () => {
    setSelectedSurveyorIds([])
    setSelectedSurveyorOptions([])
    setFilters(initialFilters)
    setCalendarDate(new Date())
    setIsFilteredByDropDown(true)
    setTargetId("#")
  }

  const setByStatus = val => {
    setFilters({ ...filters, by_status: val })
    setIsFilteredByDropDown(true)
    setTargetId("#")
  }

  const setByJobType = val => {
    setFilters({ ...filters, by_job_type: val })
    setIsFilteredByDropDown(true)
    setTargetId("#")
  }

  const onSurveyorSelectChange = val => {
    const surveyorIds = val?.map(v => v.value) || []
    setSelectedSurveyorIds(surveyorIds)
    setSelectedSurveyorOptions(val || [])
    setFilters({ ...filters, by_surveyor_ids: surveyorIds })
    setIsFilteredByDropDown(true)
    setTargetId("#")
  }

  const setBySearch = val => {
    setFilters({ ...filters, by_keyword: val })
    setTargetId("#")
  }

  useEffect(() => {
    setCalendarDateString(format(calendarDate, "d MMM yyyy"))
    setFilters({
      ...filters,
      by_booking_time: [
        startOfDay(calendarDate),
        endOfDay(addDays(calendarDate, 7))
      ]
    })
    localStorage.setItem("selectedDate", calendarDate);
    setIsFilteredByDropDown(true)
    setTargetId("#")
  }, [calendarDate])

  // Get API filters with proper surveyor_ids formatting
  const getApiFilters = () => ({
    ...filters,
    by_surveyor_ids: filters.by_surveyor_ids?.map(id => (typeof id === "string" ? parseInt(id, 10) : id)) || []
  })

  // Fetch data for a specific date (lazy load on expand or refresh)
  const fetchDataForDate = (dateKey, dateValue) => {
    setLoadingDates(prev => ({ ...prev, [dateKey]: true }))

    const apiFilters = getApiFilters()
    // Override booking time to just this specific date
    const dateStart = startOfDay(new Date(dateValue))
    const dateEnd = endOfDay(new Date(dateValue))
    const dateFilters = {
      ...apiFilters,
      by_booking_time: [dateStart, dateEnd]
    }

    getFieldWorkJobServices(baseUrl, dateFilters)
      .then(res => {
        const responseData = res?.response?.data || {}
        // Get the data for this date
        const dateData = responseData[dateKey] || null
        setDataByDate(prev => ({ ...prev, [dateKey]: dateData }))
      })
      .catch(() => {
        setDataByDate(prev => ({ ...prev, [dateKey]: null }))
      })
      .finally(() => setLoadingDates(prev => ({ ...prev, [dateKey]: false })))
  }

  // Fetch lightweight list of dates with counts (fast initial load)
  const fetchDatesList = () => {
    const fromRef = { ...(expandedDatesRef.current || {}) }
    let fromStorage = {}
    try {
      const stored = sessionStorage.getItem(SCHEDULE_EXPANDED_KEY)
      if (stored) fromStorage = JSON.parse(stored)
    } catch (_) { /* ignore */ }
    const expandedAtReloadStart = { ...fromStorage, ...fromRef }

    setInitialLoading(true)
    setDatesList([])
    setDataByDate({})

    const apiFilters = getApiFilters()

    getFieldWorkDatesList(baseUrl, apiFilters)
      .then(res => {
        if (res.status === 200) {
          const dates = res.response || []

          const newDateKeys = dates.map(d => d.key)
          const keptExpanded = Object.fromEntries(
            Object.entries(expandedAtReloadStart).filter(([key, val]) => val && newDateKeys.includes(key))
          )
          const hasPreviouslyExpanded = Object.keys(keptExpanded).length > 0
          let nextExpanded = hasPreviouslyExpanded
            ? keptExpanded
            : Object.fromEntries(dates.map(d => [d.key, true]))

          if (!hasPreviouslyExpanded) {
            setExpandAll(true)
          } else {
            setExpandAll(Object.keys(keptExpanded).length === dates.length)
          }

          // Expand and scroll to the calendar-selected date
          const calDateStr = format(startOfDay(calendarDate), "yyyy-MM-dd")
          const selectedDateEntry = dates.find(
            d => format(startOfDay(new Date(d.date)), "yyyy-MM-dd") === calDateStr
          )
          if (selectedDateEntry) {
            nextExpanded = { ...nextExpanded, [selectedDateEntry.key]: true }
            setScrollToDateKey(selectedDateEntry.key)
            try {
              sessionStorage.setItem(SCHEDULE_LAST_EXPANDED_KEY, selectedDateEntry.key)
            } catch (_) { /* ignore */ }
          }

          setDatesList(dates)
          setExpandedDates(nextExpanded)
          try {
            sessionStorage.setItem(SCHEDULE_EXPANDED_KEY, JSON.stringify(nextExpanded))
          } catch (_) { /* ignore */ }

          try {
            const lastExpanded = sessionStorage.getItem(SCHEDULE_LAST_EXPANDED_KEY)
            if (lastExpanded && nextExpanded[lastExpanded]) {
              const keyToScroll = lastExpanded
              const scrollToSection = () => {
                const element = document.getElementById(keyToScroll)
                if (element) {
                  element.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              }
              setTimeout(scrollToSection, 400)
            }
          } catch (_) { /* ignore */ }

          Object.keys(nextExpanded).forEach(key => {
            const dateItem = dates.find(d => d.key === key)
            if (dateItem) fetchDataForDate(key, dateItem.date)
          })
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
      .finally(() => setInitialLoading(false))
  }

  // Handle toggle expand/collapse for a date
  const handleExpandCollapseAll = () => {
    const nextExpandAll = !expandAll
    setExpandAll(nextExpandAll)
    if (nextExpandAll) {
      const visibleDates = datesList.filter(dateItem => !hidePast || !isPast(new Date(dateItem.date)))
      const allExpanded = Object.fromEntries(visibleDates.map(d => [d.key, true]))
      setExpandedDates(allExpanded)
      visibleDates.forEach(({ key, date }) => {
        if (dataByDate[key] === undefined) fetchDataForDate(key, date)
      })
      try {
        sessionStorage.setItem(SCHEDULE_EXPANDED_KEY, JSON.stringify(allExpanded))
      } catch (_) { /* ignore */ }
    } else {
      setExpandedDates({})
      try {
        sessionStorage.setItem(SCHEDULE_EXPANDED_KEY, JSON.stringify({}))
      } catch (_) { /* ignore */ }
    }
  }

  const handleToggleDate = (dateKey, dateValue) => {
    const isExpanded = expandedDates[dateKey]
    if (isExpanded) setExpandAll(false)
    setExpandedDates(prev => {
      const next = { ...prev, [dateKey]: !isExpanded }
      try {
        sessionStorage.setItem(SCHEDULE_EXPANDED_KEY, JSON.stringify(next))
      } catch (_) { /* ignore */ }
      return next
    })

    // Fetch data if expanding and data not yet loaded
    if (!isExpanded && dataByDate[dateKey] === undefined) {
      fetchDataForDate(dateKey, dateValue)
    }

    if (!isExpanded) {
      setScrollToDateKey(dateKey)
      try {
        sessionStorage.setItem(SCHEDULE_LAST_EXPANDED_KEY, dateKey)
      } catch (_) { /* ignore */ }
    }
  }

  // Handle refresh for a specific date
  const handleRefreshDate = (dateKey, dateValue) => {
    fetchDataForDate(dateKey, dateValue)
  }

  // Function to reload all data - now fetches dates list
  const requestJobServices = () => {
    fetchDatesList()
  }

  useEffect(() => {
    getFilteredProducts(baseUrl, "schedules")
      .then(res => {
        if (res.status === 200) {
          const products = Object.values(_.get(res.response, "product", {}))
          const allJobTypes = products.map(js => ([js.attributes.name, js.id]))
          setJobTypes(allJobTypes)
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
  }, [])

  const reloadData = () => {
    requestJobServices()
  }

  const handleSubmit = e => {
    e.preventDefault()
    reloadData()
  }

  useEffect(() => {
    reloadData()
    setIsFilteredByDropDown(false)
  }, [isFilteredByDropDown])

  const getSurveyorHandler = () => {
    if (isSurveyorsManager) {
      getAssignedSurveyorMembers(baseUrl, currentUser.id)
        .then(res => {
          if (res.status === 200) {
            const newData = res.response.data.attributes.surveyor_members.map(item => ([item.name, item.id]))
            newData.push([res.response.data.attributes.name, parseInt(res.response.data.id, 10)])

            setSurveyorOptions(newData)
            const selectOptions = newData.map(item => ({ label: item[0], value: item[1].toString() }))
            const sortedSelectOptions = _.orderBy(selectOptions, "label", "asc")
            setSurveyorSelectOptions(sortedSelectOptions)
            
            const initialIds = selectedSurveyorIds || filters.by_surveyor_ids || []
            const selectedOptions = sortedSelectOptions.filter(option => 
              initialIds.includes(option.value) || initialIds.includes(Number(option.value))
            )
            setSelectedSurveyorOptions(selectedOptions)
          }
        })
        // eslint-disable-next-line no-console
        .catch(err => console.log(err))
    } else {
      getSurveyors(baseUrl)
        .then(res => {
          if (res.status === 200) {
            const newData = res.response.map(item => ([`${item[0]} ${item[1]}`, item[2]]))
            setSurveyorOptions(newData)
            const selectOptions = newData.map(item => ({ label: item[0], value: item[1].toString() }))
            const sortedSelectOptions = _.orderBy(selectOptions, "label", "asc")
            setSurveyorSelectOptions(sortedSelectOptions)
            
            const initialIds = selectedSurveyorIds || filters.by_surveyor_ids || []
            const selectedOptions = sortedSelectOptions.filter(option => 
              initialIds.includes(option.value) || initialIds.includes(Number(option.value))
            )
            setSelectedSurveyorOptions(selectedOptions)
          }
        })
        // eslint-disable-next-line no-console
        .catch(err => console.log(err))
    }
  }

  useEffect(() => {
    getSurveyorHandler()
    fitSchedulesTablesToView()
  }, [])

  useEffect(() => {
    if (initialLoading) return undefined;

    const timer = setTimeout(() => fitSchedulesTablesToView(), 150);
    return () => clearTimeout(timer);
  }, [initialLoading, dataByDate, expandedDates])

  useEffect(() => {
    if (selectedSurveyorIds && surveyorSelectOptions.length > 0) {
      const selectedOptions = surveyorSelectOptions.filter(option => 
        selectedSurveyorIds.includes(option.value) || selectedSurveyorIds.includes(Number(option.value))
      )
      setSelectedSurveyorOptions(selectedOptions)
      setFilters({ ...filters, by_surveyor_ids: selectedSurveyorIds })
    }
  }, [selectedSurveyorIds, surveyorSelectOptions])

  useEffect(() => {
    if (targetId && initialLoading === false && targetId !== "#") {
      const element = document.getElementById(targetId)
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }, [targetId, initialLoading])

  // Scroll to expanded day section (does not use URL param)
  useEffect(() => {
    if (scrollToDateKey && !initialLoading) {
      const element = document.getElementById(scrollToDateKey)
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }
      setScrollToDateKey(null)
    }
  }, [scrollToDateKey, initialLoading])

  return (
    <div className="container-fluid mb-5">
      <div className="container-inner">
        <div className="schedules-page-sticky-header">
          <div className="d-md-flex justify-content-between align-items-center header-tabs-container">
            <div className="header-tabs">
              <h1>Schedules</h1>
            </div>

            {!readOnly && (
              <button
                className="btn btn-outlined-blue"
                onClick={() => { setShowFollowUpbookingModal(true) }}
                type="button">
                + New Booking
              </button>
            )}

            <FollowUpbookingModal
              show={showFollowUpbookingModal}
              onHide={() => { setShowFollowUpbookingModal(false) }}
              reloadData={reloadData} />

          </div>
          <div className="table-listing-filters mb-2 d-md-flex justify-content-between">
            <div>
              <div className="btn-group mr-2">
                <SearchInput value={filters.by_keyword} setValue={setBySearch} handleSubmit={handleSubmit} disableSubmit={initialLoading} />
              </div>


              <div className="btn-group mr-2">
                <FilterDropdown title="Status" filteredName={byStatusName} setFilter={setByStatus} options={jobStatusOptions} />
              </div>

              <div className="btn-group mr-2">
                <FilterDropdown title="Job Service" filteredName={byJobTypeName} setFilter={setByJobType} options={jobTypes} />
              </div>
              {(!readOnly || isSurveyorsManager || isSales) && (() => {
                const hasSelectedOptions = selectedSurveyorOptions.length > 0
                return (
                  <div className="btn-group mr-2" style={{ 
                    minWidth: hasSelectedOptions ? "300px" : "200px",
                    width: hasSelectedOptions ? "300px" : "200px"
                  }}>
                    <Select
                      isMulti
                      isSearchable
                      value={selectedSurveyorOptions}
                      onChange={onSurveyorSelectChange}
                      options={surveyorSelectOptions}
                      placeholder="Surveyor: All"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: hasSelectedOptions ? "auto" : "40px",
                          height: hasSelectedOptions ? "auto" : "30px",
                          minWidth: hasSelectedOptions ? "300px" : "200px",
                          width: hasSelectedOptions ? "300px" : "200px",
                          borderRadius: "50px",
                          border: "1px solid #ced4da",
                          boxShadow: "none",
                          backgroundColor: "#fff",
                          fontSize: "1rem",
                          fontFamily: "inherit",
                          "&:hover": {
                            border: "1px solid #ced4da"
                          },
                          ...(state.isFocused && {
                            border: "1px solid #ced4da",
                            boxShadow: "none"
                          })
                        }),
                        multiValue: (base) => ({
                          ...base,
                          borderRadius: "50px",
                          backgroundColor: "#e9ecef"
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          color: "#495057",
                          padding: "2px 8px",
                          fontSize: "1rem",
                          fontFamily: "inherit"
                        }),
                        multiValueRemove: (base) => ({
                          ...base,
                          borderRadius: "0 50px 50px 0",
                          "&:hover": {
                            backgroundColor: "#dee2e6",
                            color: "#495057"
                          }
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "#6c757d",
                          fontSize: "1rem",
                          fontFamily: "inherit",
                          margin: 0,
                          padding: 0
                        }),
                        input: (base) => ({
                          ...base,
                          margin: "0px",
                          fontSize: "1rem",
                          fontFamily: "inherit"
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: hasSelectedOptions ? "2px 12px" : "0px 12px",
                          minHeight: hasSelectedOptions ? "auto" : "30px",
                          display: "flex",
                          alignItems: "center"
                        }),
                        singleValue: (base) => ({
                          ...base,
                          fontSize: "1rem",
                          fontFamily: "inherit"
                        }),
                        option: (base) => ({
                          ...base,
                          fontSize: "1rem",
                          fontFamily: "inherit"
                        })
                      }}
                      classNamePrefix="react-select"
                    />
                  </div>
                )
              })()}

              <div className="btn-group mr-3 ml-4 mt-1">
                <input className="form-check-input" id="show-past" type="checkbox" checked={hidePast} onChange={() => { setHidePast(!hidePast) }}></input>
                <label htmlFor="show-past">Hide Past Dates</label>
              </div>

              <div className="btn-group mr-3 ml-4 mt-1">
                <input
                  className="form-check-input"
                  id="expand-all"
                  type="checkbox"
                  checked={expandAll}
                  onChange={handleExpandCollapseAll}
                />
                <label htmlFor="expand-all">Expand all</label>
              </div>

              <div className="btn-group">
                <AnchorButton
                  className="f-small"
                  onClick={clearFilters}>
                  Clear Filters
                </AnchorButton>
              </div>
            </div>

            <Calendar startDate={filters.by_booking_time[0]} endDate={filters.by_booking_time[1]} calendarDate={calendarDate} setCalendarDate={setCalendarDate} />
          </div>
        </div>

        {initialLoading ? <Loader /> : (
          <>
            {datesList
              .filter(dateItem => !hidePast || !isPast(new Date(dateItem.date)))
              .map(dateItem => {
              const dateKey = dateItem.key
              const dateValue = dateItem.date
              const isExpanded = expandedDates[dateKey] || false
              const isLoading = loadingDates[dateKey] || false
              const dateData = dataByDate[dateKey]

              return (
                <CollapsibleSection
                  key={dateKey}
                  id={dateKey}
                  name={dateItem.date_display}
                  count={dateItem.count}
                  isExpanded={isExpanded}
                  isLoading={isLoading}
                  onToggle={() => handleToggleDate(dateKey, dateValue)}
                  onRefresh={() => handleRefreshDate(dateKey, dateValue)}>
                  {dateData ? (
                    <DateSectionContent
                      dateKey={dateKey}
                      dateData={dateData}
                      calendarDateString={calendarDateString}
                      surveyorOptions={surveyorOptions}
                      readOnly={readOnly}
                      reloadData={() => handleRefreshDate(dateKey, dateValue)} />
                  ) : (
                    !isLoading && <p className="text-muted py-3 px-2">No jobs found for this date.</p>
                  )}
                </CollapsibleSection>
              )
            })}
            {datesList.length === 0 && (
              <p className="text-muted py-3">No scheduled jobs found for the selected date range.</p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Component for date section content (block outs, public holidays, job services table)
const DateSectionContent = ({
  dateKey,
  dateData,
  calendarDateString,
  surveyorOptions,
  readOnly,
  reloadData
}) => {
  const assignmentsPerDay = dateData?.assignments?.data
  const dailyBlockOuts = dateData?.block_outs
  const todaysPublicHoliday = dateData?.public_holidays?.[0]
  const isSunday = dateKey?.includes("Sunday")
  const sortedAssignmentsPerDay = _.orderBy(assignmentsPerDay, ["attributes.surveyor_name", "attributes.start_at"], ["asc", "asc"])

  return (
    <div className="px-2">
      {/* Public holidays */}
      {todaysPublicHoliday && (
        <div className="mb-2">
          <h6 className="mt-3 mb-0 d-inline text-danger">Public Holiday: </h6>
          <span>{todaysPublicHoliday}</span>
        </div>
      )}

      {/* Surveyor leave */}
      {(!!dailyBlockOuts?.length && !todaysPublicHoliday && !isSunday) && (
        <BlockOutsTable blockOuts={dailyBlockOuts} currentDate={dateData?.date} reloadData={reloadData} />
      )}

      {/* Job Services */}
      {!!sortedAssignmentsPerDay?.length && (
        <>
          <div className="d-flex align-items-center mt-3 mb-0">
            <h6 className="mb-0">Jobs</h6>
            {dateData?.date && (
              <Link
                to={`/schedules/map-views?scheduled_date=${dateData.date}`}
                className="ml-3 small text-primary">
                Show on Map
              </Link>
            )}
          </div>
          <SchedulesDateTable
            jobServicesDay={sortedAssignmentsPerDay}
            calendarDateString={calendarDateString}
            reloadData={reloadData}
            surveyorOptions={surveyorOptions}
            readOnly={readOnly} />
        </>
      )}

      {!assignmentsPerDay?.length && !dailyBlockOuts?.length && !todaysPublicHoliday && (
        <p className="text-muted py-2">No jobs scheduled for this date.</p>
      )}
    </div>
  )
}
